/**
 * Interactive European energy price visualization with time scrubber
 */

import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
  Legend,
} from "recharts";
import { useEuroData, useEuroStats, useEuroLoadData, useMoldovaLiveLoad } from "../hooks/useEuroData";
import { calculateTimeWeightedAveragePrice, simulateMarket } from "../utils/euroDataAnalysis";
import EuroPriceMap from "./EuroPriceMap";

const EuroPriceViewer = () => {
  const { data, isLoading, error } = useEuroData();
  const { loadData, isLoading: isLoadingLoad, error: loadError } = useEuroLoadData();
  const { liveData, isLoading: isLoadingLive, error: liveError } = useMoldovaLiveLoad();
  const stats = useEuroStats(data);

  // Range selection: [startTimestamp, endTimestamp]
  const [rangeValues, setRangeValues] = useState<[number, number]>([0, 0]);

  // Chart interaction state
  const chartRef = useRef<HTMLDivElement>(null);
  const [dragState, setDragState] = useState<{
    type: 'move' | 'resize-left' | 'resize-right' | null;
    startX: number;
    startRange: [number, number];
  } | null>(null);
  const [hoverType, setHoverType] = useState<'move' | 'resize-left' | 'resize-right' | null>(null);

  // Price calculation options
  const [noNegativeRebate, setNoNegativeRebate] = useState(false);

  // Controls visibility
  const [showControls, setShowControls] = useState(false);

  // Felt map ID (can be configured)
  const feltMapId = 'SV9AtQcwuRI2JJV1RZMkG9AC';

  // Simulation parameters
  const [generationCost, setGenerationCost] = useState(120); // EUR/MWh (PPA price)
  const baseCapacityMW = 200; // MW - fixed base capacity
  const [plantCapacityMW, setPlantCapacityMW] = useState(100); // MW - plant generation capacity

  // Solar (intermittent) capacity parameters - 10 hours of peak solar per day
  const [solarCapacityMW, setSolarCapacityMW] = useState(0); // MW - solar capacity
  const solarStartHour = 8; // 8 AM UTC (fixed)
  const solarEndHour = 18; // 6 PM UTC (fixed) - 10 hours total
  const [solarPrice, setSolarPrice] = useState(50); // EUR/MWh - solar generation cost

  // Get min/max timestamps from data
  const timeRange = useMemo(() => {
    if (!data || data.length === 0) return null;
    return {
      min: data[0].startTime.getTime(),
      max: data[data.length - 1].startTime.getTime(), // Use startTime to match chart domain
    };
  }, [data]);

  // Initialize range from URL or default to last 1 year when data loads
  useEffect(() => {
    if (data && data.length > 0 && timeRange) {
      const params = new URLSearchParams(window.location.search);
      const startParam = params.get("start");
      const endParam = params.get("end");

      if (startParam && endParam) {
        // Try to parse as ISO date strings
        const startDate = new Date(startParam);
        const endDate = new Date(endParam);

        if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
          const start = Math.max(timeRange.min, startDate.getTime());
          const end = Math.min(timeRange.max, endDate.getTime());
          if (start < end) {
            setRangeValues([start, end]);
            return;
          }
        }
      }

      // Default: last 1 year of data
      const oneYearMs = 365 * 24 * 60 * 60 * 1000;
      const start = Math.max(timeRange.min, timeRange.max - oneYearMs);
      setRangeValues([start, timeRange.max]);
    }
  }, [data, timeRange]);

  // Update URL when range changes (debounced to avoid too many history entries)
  useEffect(() => {
    if (!data || data.length === 0 || !timeRange) return;
    if (rangeValues[0] === 0 && rangeValues[1] === 0) return; // Skip uninitialized state

    const timeoutId = setTimeout(() => {
      const url = new URL(window.location.href);
      url.searchParams.set("start", new Date(rangeValues[0]).toISOString());
      url.searchParams.set("end", new Date(rangeValues[1]).toISOString());
      window.history.replaceState({}, "", url.toString());
    }, 300); // Debounce by 300ms

    return () => clearTimeout(timeoutId);
  }, [rangeValues, data, timeRange]);

  // Convert pixel X position to timestamp based on chart dimensions
  const pixelToTimestamp = useCallback((pixelX: number): number | null => {
    if (!chartRef.current || !timeRange) return null;

    const chartContainer = chartRef.current.querySelector('.recharts-wrapper');
    if (!chartContainer) return null;

    const rect = chartContainer.getBoundingClientRect();
    const chartWidth = rect.width;

    // Account for chart margins (approximate values based on typical Recharts layout)
    const leftMargin = 65; // Approximate left margin for Y-axis
    const rightMargin = 65; // Approximate right margin for right Y-axis
    const plotWidth = chartWidth - leftMargin - rightMargin;

    const relativeX = pixelX - rect.left - leftMargin;
    const ratio = Math.max(0, Math.min(1, relativeX / plotWidth));

    return timeRange.min + ratio * (timeRange.max - timeRange.min);
  }, [timeRange]);

  // Handle mouse move on chart to update cursor
  const handleChartMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!timeRange || rangeValues[0] === 0 || rangeValues[1] === 0 || dragState) return;

    const timestamp = pixelToTimestamp(e.clientX);
    if (timestamp === null) return;

    const edgeThreshold = (timeRange.max - timeRange.min) * 0.02; // 2% of total range

    if (Math.abs(timestamp - rangeValues[0]) < edgeThreshold) {
      setHoverType('resize-left');
    } else if (Math.abs(timestamp - rangeValues[1]) < edgeThreshold) {
      setHoverType('resize-right');
    } else if (timestamp >= rangeValues[0] && timestamp <= rangeValues[1]) {
      setHoverType('move');
    } else {
      setHoverType(null);
    }
  }, [timeRange, rangeValues, pixelToTimestamp, dragState]);

  // Handle mouse down on chart to initiate drag
  const handleChartMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!timeRange || rangeValues[0] === 0 || rangeValues[1] === 0) return;

    const timestamp = pixelToTimestamp(e.clientX);
    if (timestamp === null) return;

    const edgeThreshold = (timeRange.max - timeRange.min) * 0.02; // 2% of total range

    // Determine what to drag
    let dragType: 'move' | 'resize-left' | 'resize-right' | null = null;

    if (Math.abs(timestamp - rangeValues[0]) < edgeThreshold) {
      dragType = 'resize-left';
    } else if (Math.abs(timestamp - rangeValues[1]) < edgeThreshold) {
      dragType = 'resize-right';
    } else if (timestamp >= rangeValues[0] && timestamp <= rangeValues[1]) {
      dragType = 'move';
    }

    if (dragType) {
      e.preventDefault();
      setDragState({
        type: dragType,
        startX: e.clientX,
        startRange: [...rangeValues] as [number, number],
      });
    }
  }, [timeRange, rangeValues, pixelToTimestamp]);

  // Handle mouse move during drag
  useEffect(() => {
    if (!dragState || !timeRange) return;

    const handleMouseMove = (e: MouseEvent) => {
      const currentTimestamp = pixelToTimestamp(e.clientX);
      if (currentTimestamp === null) return;

      const startTimestamp = pixelToTimestamp(dragState.startX);
      if (startTimestamp === null) return;

      const timeDelta = currentTimestamp - startTimestamp;

      let newStart = dragState.startRange[0];
      let newEnd = dragState.startRange[1];

      if (dragState.type === 'move') {
        // Move entire range
        newStart = dragState.startRange[0] + timeDelta;
        newEnd = dragState.startRange[1] + timeDelta;

        // Clamp to bounds
        const rangeSize = dragState.startRange[1] - dragState.startRange[0];
        if (newStart < timeRange.min) {
          newStart = timeRange.min;
          newEnd = newStart + rangeSize;
        }
        if (newEnd > timeRange.max) {
          newEnd = timeRange.max;
          newStart = newEnd - rangeSize;
        }
      } else if (dragState.type === 'resize-left') {
        // Resize left edge
        newStart = Math.max(timeRange.min, Math.min(dragState.startRange[1] - 3600000, dragState.startRange[0] + timeDelta));
        newEnd = dragState.startRange[1];
      } else if (dragState.type === 'resize-right') {
        // Resize right edge
        newStart = dragState.startRange[0];
        newEnd = Math.min(timeRange.max, Math.max(dragState.startRange[0] + 3600000, dragState.startRange[1] + timeDelta));
      }

      if (newStart < newEnd) {
        setRangeValues([newStart, newEnd]);
      }
    };

    const handleMouseUp = () => {
      setDragState(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, timeRange, pixelToTimestamp]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!data) return [];

    // Create a map of timestamps to load values for quick lookup
    // Use a tolerance window for matching timestamps (within 30 minutes)
    const loadMap = new Map<number, number>();
    const loadTimeArray: Array<{ time: number; load: number }> = [];

    if (loadData) {
      loadData.forEach((record) => {
        if (record.actualLoad !== undefined) {
          const timestamp = record.startTime.getTime();
          loadMap.set(timestamp, record.actualLoad);
          loadTimeArray.push({ time: timestamp, load: record.actualLoad });
        }
      });
    }

    // Sample data for performance (show every Nth point on large datasets)
    const sampleRate = Math.max(1, Math.floor(data.length / 2000));

    return data
      .filter((_, idx) => idx % sampleRate === 0)
      .map((record) => {
        const timestamp = record.startTime.getTime();

        // Try exact match first
        let load = loadMap.get(timestamp);

        // If no exact match, find closest within 30 minutes
        if (load === undefined && loadTimeArray.length > 0) {
          const tolerance = 30 * 60 * 1000; // 30 minutes in milliseconds
          const closest = loadTimeArray.find(
            (item) => Math.abs(item.time - timestamp) < tolerance
          );
          if (closest) {
            load = closest.load;
          }
        }

        return {
          timestamp,
          price: record.dayAheadPrice,
          load,
          date: record.startTime,
        };
      });
  }, [data, loadData]);

  // Calculate range statistics
  const rangeStats = useMemo(() => {
    if (!data || data.length === 0) return null;
    if (!timeRange) return null;
    if (rangeValues[0] >= rangeValues[1]) return null;

    // Use the utility function for time-weighted average
    const result = calculateTimeWeightedAveragePrice(
      data,
      new Date(rangeValues[0]),
      new Date(rangeValues[1]),
      "dayAhead",
      noNegativeRebate
    );

    if (!result) return null;

    return {
      count: result.recordCount,
      startTime: result.actualStartTime,
      endTime: result.actualEndTime,
      avgPrice: result.averagePrice,
      startTimestamp: rangeValues[0],
      endTimestamp: rangeValues[1],
    };
  }, [data, rangeValues, timeRange, noNegativeRebate]);

  // Calculate simulation results
  const simulationResults = useMemo(() => {
    if (!data || data.length === 0) return null;
    if (!loadData || loadData.length === 0) return null;
    if (!timeRange) return null;
    if (rangeValues[0] >= rangeValues[1]) return null;

    const result = simulateMarket(
      data,
      loadData,
      new Date(rangeValues[0]),
      new Date(rangeValues[1]),
      {
        generationCost,
        baseCapacityMW,
        plantCapacityMW,
        priceType: "dayAhead",
        noNegativeRebate,
        intermittentCapacity: solarCapacityMW > 0 ? [{
          capacity: solarCapacityMW,
          startTime: solarStartHour,
          endTime: solarEndHour,
          price: solarPrice,
        }] : undefined,
      }
    );

    return result;
  }, [data, loadData, rangeValues, timeRange, generationCost, baseCapacityMW, plantCapacityMW, noNegativeRebate, solarCapacityMW, solarStartHour, solarEndHour, solarPrice]);

  // Format date for display
  const formatDateTime = (date: Date) => {
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
    });
  };

  // Format date for axis
  const formatAxisDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  if (isLoading || isLoadingLoad) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          fontSize: "18px",
          color: "#666",
        }}
      >
        Loading European energy data...
      </div>
    );
  }

  if (error || loadError) {
    return (
      <div
        style={{
          padding: "20px",
          background: "#fee",
          border: "1px solid #fcc",
          borderRadius: "8px",
          color: "#c00",
        }}
      >
        Error loading data: {error?.message || loadError?.message}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <div>No data available</div>;
  }

  return (
    <div
      style={{
        padding: "20px",
        background: "white",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      {/* Header with stats */}
      <div style={{ marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
          <h2 style={{ margin: "0", fontSize: "24px", color: "#333" }}>
            Moldovan Energy Prices & Load
          </h2>
          <button
            type="button"
            onClick={() => setShowControls(!showControls)}
            style={{
              background: showControls ? "#e0e0e0" : "transparent",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "8px 12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
            title={showControls ? "Hide controls" : "Show controls"}
          >
            <span style={{ fontSize: "14px", fontWeight: "500" }}>Parameters</span>
            <span
              style={{
                fontSize: "18px",
                transition: "transform 0.3s ease-in-out",
                transform: showControls ? "rotate(90deg)" : "rotate(0deg)",
                display: "inline-block",
              }}
            >
              ⚙️
            </span>
          </button>
        </div>
        {stats && (
          <div
            style={{
              display: "flex",
              gap: "20px",
              fontSize: "14px",
              color: "#666",
            }}
          >
            <div>
              <strong>€{stats.avgPrice.toFixed(2)}</strong>/MWh avg
            </div>
            {liveData && (
              <div>
                <strong>{liveData.consumption_mw !== null ? liveData.consumption_mw.toLocaleString() : '-'}</strong> MW consumption
              </div>
            )}
            {liveData && (
              <div>
                <strong>{liveData.generation_mw !== null ? liveData.generation_mw.toLocaleString() : '-'}</strong> MW generation
              </div>
            )}
            <div style={{ marginLeft: "auto", color: "#999" }}>
              {formatDateTime(stats.startDate)} → {formatDateTime(stats.endDate)}
            </div>
          </div>
        )}
      </div>

      {/* Control section */}
      <div
        style={{
          maxHeight: showControls ? "1000px" : "0",
          opacity: showControls ? 1 : 0,
          overflow: "hidden",
          transition: "max-height 0.4s ease-in-out, opacity 0.3s ease-in-out, margin-bottom 0.4s ease-in-out",
          marginBottom: showControls ? "20px" : "0",
        }}
      >
        <div
          style={{
            padding: "15px",
            background: "#f8f9fa",
            border: "1px solid #e0e0e0",
          }}
        >
          <div style={{ fontSize: "14px", fontWeight: "600", marginBottom: "10px", color: "#333" }}>
            Price Calculation Options
          </div>
          <div style={{ display: "flex", gap: "20px", alignItems: "center", marginBottom: "15px" }}>
            <label style={{ display: "flex", alignItems: "center", cursor: "pointer", fontSize: "14px" }}>
              <input
                type="radio"
                name="negativePrice"
                checked={!noNegativeRebate}
                onChange={() => setNoNegativeRebate(false)}
                style={{ marginRight: "8px", cursor: "pointer" }}
              />
              <span>Include negative prices (allow rebates)</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", cursor: "pointer", fontSize: "14px" }}>
              <input
                type="radio"
                name="negativePrice"
                checked={noNegativeRebate}
                onChange={() => setNoNegativeRebate(true)}
                style={{ marginRight: "8px", cursor: "pointer" }}
              />
              <span>Treat negative prices as €0 (no rebate)</span>
            </label>
          </div>

          <div style={{ fontSize: "14px", fontWeight: "600", marginBottom: "10px", color: "#333" }}>
            Simulation Parameters
          </div>
          <div style={{ display: "flex", gap: "20px", alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", fontSize: "14px" }}>
              <span style={{ marginRight: "8px" }}>Generation Cost (PPA):</span>
              <input
                type="number"
                value={generationCost}
                onChange={(e) => setGenerationCost(Number(e.target.value))}
                min="0"
                step="1"
                style={{
                  width: "80px",
                  padding: "4px 8px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              />
              <span style={{ marginLeft: "4px" }}>€/MWh</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", fontSize: "14px" }}>
              <span style={{ marginRight: "8px" }}>Plant Capacity:</span>
              <input
                type="number"
                value={plantCapacityMW}
                onChange={(e) => setPlantCapacityMW(Number(e.target.value))}
                min="0"
                step="10"
                style={{
                  width: "80px",
                  padding: "4px 8px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              />
              <span style={{ marginLeft: "4px" }}>MW</span>
            </label>
          </div>

          <div style={{ fontSize: "14px", fontWeight: "600", marginTop: "15px", marginBottom: "10px", color: "#333", display: "flex", alignItems: "center", gap: "8px" }}>
            ☀️ Solar Capacity (8 AM - 6 PM UTC)
          </div>
          <div style={{ display: "flex", gap: "20px", alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", fontSize: "14px" }}>
              <span style={{ marginRight: "8px" }}>Solar Capacity:</span>
              <input
                type="number"
                value={solarCapacityMW}
                onChange={(e) => setSolarCapacityMW(Number(e.target.value))}
                min="0"
                step="10"
                style={{
                  width: "80px",
                  padding: "4px 8px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              />
              <span style={{ marginLeft: "4px" }}>MW</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", fontSize: "14px" }}>
              <span style={{ marginRight: "8px" }}>Solar Cost:</span>
              <input
                type="number"
                value={solarPrice}
                onChange={(e) => setSolarPrice(Number(e.target.value))}
                min="0"
                step="1"
                style={{
                  width: "80px",
                  padding: "4px 8px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              />
              <span style={{ marginLeft: "4px" }}>€/MWh</span>
            </label>
            <div style={{ display: "flex", alignItems: "center", fontSize: "12px", color: "#999", fontStyle: "italic" }}>
              (10 hours of peak generation per day)
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div style={{ margin: "0 0 20px 0", height: "400px" }}>
        <EuroPriceMap mapId={feltMapId} />
      </div>

      {/* Chart */}
      <div
        ref={chartRef}
        onMouseDown={handleChartMouseDown}
        onMouseMove={handleChartMouseMove}
        role="application"
        aria-label="Interactive price chart"
        style={{ outline: 'none', userSelect: 'none' }}
      >
        <ResponsiveContainer width="100%" height={300} style={{ outline: 'none' }}>
          <LineChart data={chartData} style={{ outline: 'none' }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="timestamp"
              type="number"
              domain={["dataMin", "dataMax"]}
              tickFormatter={formatAxisDate}
              stroke="#666"
              style={{ fontSize: "12px" }}
            />
            <YAxis
              yAxisId="left"
              label={{
                value: "Price (EUR/MWh)",
                angle: -90,
                position: "insideLeft",
                style: { fill: "#2563EB" },
              }}
              stroke="#2563EB"
              style={{ fontSize: "12px" }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              label={{
                value: "Load (MW)",
                angle: 90,
                position: "insideRight",
                style: { fill: "#FACC15" },
              }}
              stroke="#FACC15"
              style={{ fontSize: "12px" }}
              domain={["auto", "auto"]}
              allowDataOverflow={false}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div
                      style={{
                        background: "rgba(255, 255, 255, 0.95)",
                        padding: "10px",
                        border: "1px solid #ccc",
                        fontSize: "12px",
                      }}
                    >
                      <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                        {formatDateTime(data.date)}
                      </div>
                      <div style={{ color: "#2563EB" }}>
                        Price: €{data.price.toFixed(2)}/MWh
                      </div>
                      {data.load !== undefined && (
                        <div style={{ color: "#FACC15", marginTop: "2px" }}>
                          Load: {data.load.toFixed(0)} MW
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend wrapperStyle={{ fontSize: "12px" }} iconType="line" />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="price"
              name="Price (EUR/MWh)"
              stroke="#2563EB"
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
              connectNulls={true}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="load"
              name="Load (MW)"
              stroke="#FACC15"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls={true}
            />
            {/* Highlight selected range */}
            {rangeStats && (
              <ReferenceArea
                key={`${rangeStats.startTimestamp}-${rangeStats.endTimestamp}`}
                x1={rangeStats.startTimestamp}
                x2={rangeStats.endTimestamp}
                fill="#2563EB"
                fillOpacity={0.2}
                stroke="#2563EB"
                strokeWidth={2}
                ifOverflow={"visible"}
                yAxisId="left"
                style={{
                  cursor: dragState
                    ? (dragState.type === 'resize-left' || dragState.type === 'resize-right')
                      ? 'ew-resize'
                      : 'grabbing'
                    : hoverType === 'resize-left' || hoverType === 'resize-right'
                      ? 'ew-resize'
                      : 'grab'
                }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Plant perspective statistics */}
      {simulationResults && (
        <div
          style={{
            margin: "20px 0",
            padding: "20px",
            background: "#E5E5E5",
            color: "#1e293b",
          }}
        >
          <div style={{ marginBottom: "15px" }}>
            <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "4px" }}>
              Plant Performance
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "20px",
            }}
          >
            <div>
              <div style={{ fontSize: "12px", opacity: 0.8, marginBottom: "4px" }}>
                Gas Revenue
              </div>
              <div style={{ fontSize: "32px", fontWeight: "bold" }}>
                €{(simulationResults.totalPlantRevenueFromLoad + simulationResults.totalPlantRevenueFromMarket).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </div>
              <div style={{ fontSize: "11px", opacity: 0.7, marginTop: "4px" }}>
                €{simulationResults.totalPlantRevenueFromLoad.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} from load + €{simulationResults.totalPlantRevenueFromMarket.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} from market
              </div>
            </div>
            <div>
              <div style={{ fontSize: "12px", opacity: 0.8, marginBottom: "4px" }}>
                Gas Generation
              </div>
              <div style={{ fontSize: "32px", fontWeight: "bold" }}>
                {simulationResults.totalPlantGenerated.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                <span style={{ fontSize: "16px", fontWeight: "normal", marginLeft: "4px" }}>
                  MWh
                </span>
              </div>
              <div style={{ fontSize: "11px", opacity: 0.7, marginTop: "4px" }}>
                total gas plant output
              </div>
            </div>
            {simulationResults.totalIntermittentGenerated > 0 && (
              <>
                <div>
                  <div style={{ fontSize: "12px", opacity: 0.8, marginBottom: "4px" }}>
                    ☀️ Solar Revenue
                  </div>
                  <div style={{ fontSize: "32px", fontWeight: "bold" }}>
                    €{(simulationResults.revenue - simulationResults.totalPlantRevenueFromMarket).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </div>
                  <div style={{ fontSize: "11px", opacity: 0.7, marginTop: "4px" }}>
                    profit from solar sales to market
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "12px", opacity: 0.8, marginBottom: "4px" }}>
                    ☀️ Solar Generation
                  </div>
                  <div style={{ fontSize: "32px", fontWeight: "bold" }}>
                    {simulationResults.totalIntermittentGenerated.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                    <span style={{ fontSize: "16px", fontWeight: "normal", marginLeft: "4px" }}>
                      MWh
                    </span>
                  </div>
                  <div style={{ fontSize: "11px", opacity: 0.7, marginTop: "4px" }}>
                    {simulationResults.totalIntermittentSold.toFixed(1)} MWh sold
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}


      {/* Stats view */}
      {rangeStats && (
        <div
          style={{
            margin: "20px 0",
            padding: "20px",
            background: "#BFDBFE",
            color: "#1e293b",
          }}
        >
          <div style={{ marginBottom: "15px" }}>
            <div style={{ fontSize: "16px", fontWeight: "600" }}>
              HDVC Line
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "15px",
            }}
          >
            <div>
              <div style={{ fontSize: "12px", opacity: 0.8, marginBottom: "4px" }}>
                Average Price
              </div>
              <div style={{ fontSize: "28px", fontWeight: "bold" }}>
                €{rangeStats.avgPrice.toFixed(2)}
                <span style={{ fontSize: "14px", fontWeight: "normal" }}>
                  /MWh
                </span>
              </div>
            </div>
            {simulationResults && (
              <>
                <div>
                  <div style={{ fontSize: "12px", opacity: 0.8, marginBottom: "4px" }}>
                    Simulated Profit
                  </div>
                  <div style={{ fontSize: "28px", fontWeight: "bold" }}>
                    €{simulationResults.profit.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "12px", opacity: 0.8, marginBottom: "4px" }}>
                    Savings
                  </div>
                  <div style={{ fontSize: "28px", fontWeight: "bold" }}>
                    €{simulationResults.savings.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </div>
                  <div style={{ fontSize: "11px", opacity: 0.7 }}>
                    {simulationResults.totalEnergyBought.toFixed(1)} MWh bought
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "12px", opacity: 0.8, marginBottom: "4px" }}>
                    Revenue
                  </div>
                  <div style={{ fontSize: "28px", fontWeight: "bold" }}>
                    €{simulationResults.revenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </div>
                  <div style={{ fontSize: "11px", opacity: 0.7 }}>
                    {simulationResults.totalEnergySold.toFixed(1)} MWh sold
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Live Load Data Box */}
      <div
        style={{
          margin: "20px 0 0 0",
          padding: "20px",
          background: "#F0FDF4",
          color: "#1e293b",
        }}
      >
        <div style={{ marginBottom: "15px" }}>
          <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "4px" }}>
            🔌 Current Moldova Grid Status
          </div>
          {liveData && (
            <div style={{ fontSize: "11px", opacity: 0.7 }}>
              Last updated: {new Date(liveData.timestamp).toLocaleString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                timeZone: "UTC",
              })} UTC
            </div>
          )}
        </div>

        {isLoadingLive && !liveData && (
          <div style={{ fontSize: "14px", opacity: 0.7 }}>
            Loading current grid data...
          </div>
        )}

        {liveError && (
          <div style={{ fontSize: "14px", color: "#dc2626" }}>
            Error loading live data: {liveError.message}
          </div>
        )}

        {liveData && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "15px",
                marginBottom: "20px",
              }}
            >
              <div>
                <div style={{ fontSize: "12px", opacity: 0.8, marginBottom: "4px" }}>
                  Total Load
                </div>
                <div style={{ fontSize: "28px", fontWeight: "bold" }}>
                  {liveData.consumption_mw !== null ? liveData.consumption_mw.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '-'}
                  <span style={{ fontSize: "14px", fontWeight: "normal" }}>
                    {" "}MW
                  </span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: "12px", opacity: 0.8, marginBottom: "4px" }}>
                  Total Generation
                </div>
                <div style={{ fontSize: "28px", fontWeight: "bold" }}>
                  {liveData.generation_mw !== null ? liveData.generation_mw.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '-'}
                  <span style={{ fontSize: "14px", fontWeight: "normal" }}>
                    {" "}MW
                  </span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: "12px", opacity: 0.8, marginBottom: "4px" }}>
                  🇷🇴 Inflows (Romania)
                </div>
                <div style={{ fontSize: "28px", fontWeight: "bold", color: "#10b981" }}>
                  {liveData.flow_md_ro_mw !== null ? liveData.flow_md_ro_mw.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '-'}
                  <span style={{ fontSize: "14px", fontWeight: "normal" }}>
                    {" "}MW
                  </span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: "12px", opacity: 0.8, marginBottom: "4px" }}>
                  🇺🇦 Outflows (Ukraine)
                </div>
                <div style={{ fontSize: "28px", fontWeight: "bold", color: "#ef4444" }}>
                  {liveData.flow_md_ua_mw !== null ? (-liveData.flow_md_ua_mw).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '-'}
                  <span style={{ fontSize: "14px", fontWeight: "normal" }}>
                    {" "}MW
                  </span>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: "10px", paddingTop: "10px", borderTop: "1px solid rgba(0,0,0,0.1)" }}>
              <div style={{ fontSize: "14px", fontWeight: "600", marginBottom: "10px", opacity: 0.8 }}>
                Generation Sources
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                  gap: "12px",
                }}
              >
                <div>
                  <div style={{ fontSize: "11px", opacity: 0.8, marginBottom: "4px" }}>
                    🔥 General Thermal
                  </div>
                  <div style={{ fontSize: "20px", fontWeight: "bold" }}>
                    {liveData.thermal_mw !== null ? liveData.thermal_mw.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '-'}
                    <span style={{ fontSize: "12px", fontWeight: "normal" }}>
                      {" "}MW
                    </span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", opacity: 0.8, marginBottom: "4px" }}>
                    💧 Hydro
                  </div>
                  <div style={{ fontSize: "20px", fontWeight: "bold" }}>
                    {liveData.hydro_mw !== null ? liveData.hydro_mw.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '-'}
                    <span style={{ fontSize: "12px", fontWeight: "normal" }}>
                      {" "}MW
                    </span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", opacity: 0.8, marginBottom: "4px" }}>
                    ☀️ Solar
                  </div>
                  <div style={{ fontSize: "20px", fontWeight: "bold" }}>
                    {liveData.solar_mw !== null ? liveData.solar_mw.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '-'}
                    <span style={{ fontSize: "12px", fontWeight: "normal" }}>
                      {" "}MW
                    </span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", opacity: 0.8, marginBottom: "4px" }}>
                    💨 Wind
                  </div>
                  <div style={{ fontSize: "20px", fontWeight: "bold" }}>
                    {liveData.wind_mw !== null ? liveData.wind_mw.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '-'}
                    <span style={{ fontSize: "12px", fontWeight: "normal" }}>
                      {" "}MW
                    </span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", opacity: 0.8, marginBottom: "4px" }}>
                    🌱 Biogas
                  </div>
                  <div style={{ fontSize: "20px", fontWeight: "bold" }}>
                    {liveData.biogas_mw !== null ? liveData.biogas_mw.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '-'}
                    <span style={{ fontSize: "12px", fontWeight: "normal" }}>
                      {" "}MW
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EuroPriceViewer;
